import {OnDestroy} from '@angular/core';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {isStandardVersionConflict, savedStandardVersion, StandardVersionSnapshot,
  standardResponseWithVersion} from './standard-version';

const NO_ERROR = '';
const ACCEPTED_STATUSES = [200, 201, 204];
const FIRST_STATUS_INDEX = 0;
const ERROR_KEY = {conflict: 'STANDARD_VERSION_CONFLICT_MESSAGE', failed: 'STANDARD_WRITE_FAILED'};

export class StandardWriteAction implements OnDestroy {
  pending = false;
  requiresReload = false;
  reloadFailed = false;
  errorKey = NO_ERROR;
  private destroyed = false;
  private cancelled$ = new Subject<void>();

  run<S extends StandardVersionSnapshot, T>(source: S, command: (snapshot: S) => Observable<HttpResponse<T>>,
                                          accept: (response: HttpResponse<T>) => void): void {
    if (this.pending || this.requiresReload || this.destroyed || !source) { return; }
    const originalVersion = source.version;
    const snapshot = Object.assign({}, source);
    this.pending = true;
    this.errorKey = NO_ERROR;
    command(snapshot).takeUntil(this.cancelled$).subscribe(response => {
      if (!this.acceptVersion(source, originalVersion, response)) { return; }
      this.pending = false;
      accept(response);
    }, error => this.reject(error), () => { this.pending = false; });
  }

  reload<S extends StandardVersionSnapshot>(source: S, request: Observable<HttpResponse<StandardVersionSnapshot>>,
                                            accept: (standard: S) => void): void {
    if (this.pending || this.destroyed || !source) { return; }
    this.pending = true;
    this.reloadFailed = false;
    request.takeUntil(this.cancelled$).subscribe(response => {
      this.pending = false;
      const standard = standardResponseWithVersion(response).body;
      if (!standard || standard.id !== source.id || standard.version == null) { this.reloadFailed = true; return; }
      this.reset();
      accept(standard as S);
    }, () => { this.pending = false; this.reloadFailed = true; });
  }

  reject(error?: HttpErrorResponse): void {
    this.pending = false;
    this.requiresReload = true;
    this.errorKey = isStandardVersionConflict(error) ? ERROR_KEY.conflict : ERROR_KEY.failed;
  }

  reset(): void {
    this.cancelled$.next();
    this.pending = this.requiresReload = this.reloadFailed = false;
    this.errorKey = NO_ERROR;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.cancelled$.next();
    this.cancelled$.complete();
  }

  private acceptVersion<T>(source: StandardVersionSnapshot, originalVersion: number,
                           response: HttpResponse<T>): boolean {
    const version = savedStandardVersion(response);
    if (ACCEPTED_STATUSES.indexOf(response.status) < FIRST_STATUS_INDEX || version == null) { this.reject(); return false; }
    if (source.version !== originalVersion) {
      this.reject();
      this.errorKey = ERROR_KEY.conflict;
      return false;
    }
    source.version = version;
    return true;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/standard-write-action.ts